// Shade-swatch variant picker with colour group filters (snippets/variant-shade-grid.liquid).
// Builds on Dawn's <variant-selects> from global.js, so variant id, price, URL,
// media, share link and the add to bag button all update the same way as the
// dropdown picker. Only how the selected option is read differs.
(() => {
  const defineVariantShadeGrid = () => {
    if (customElements.get('variant-shade-grid')) return;
    const VariantSelects = customElements.get('variant-selects');
    if (!VariantSelects) return;

    class VariantShadeGrid extends VariantSelects {
      constructor() {
        super();
        // Colour group tabs: <li> slides inside Dawn's <slider-component>, same as the main PDP.
        this.filterButtons = Array.from(this.querySelectorAll('.filters li[data-group]'));
        this.items = Array.from(this.querySelectorAll('.variant-shade-grid__item'));
        this.selectedLabel = this.querySelector('[data-selected-shade]');
        this.onFilterClick = this.onFilterClick.bind(this);
        this.onFilterKeydown = this.onFilterKeydown.bind(this);
        this.onFormSubmit = this.onFormSubmit.bind(this);
      }

      connectedCallback() {
        this.filterButtons.forEach((button) => {
          button.addEventListener('click', this.onFilterClick);
          button.addEventListener('keydown', this.onFilterKeydown);
        });
        // Capture phase on the document runs before product-form's own submit
        // handler, so an add to bag without a shade can be stopped here.
        document.addEventListener('submit', this.onFormSubmit, true);
        this.setActiveGroup(this.dataset.activeGroup || 'All');
        this.scrollActiveFilterIntoView();
      }

      disconnectedCallback() {
        this.filterButtons.forEach((button) => {
          button.removeEventListener('click', this.onFilterClick);
          button.removeEventListener('keydown', this.onFilterKeydown);
        });
        document.removeEventListener('submit', this.onFormSubmit, true);
      }

      onVariantChange(event) {
        if (!event.target.matches('.variant-shade-grid__input')) return;
        super.onVariantChange(event);
        this.syncSelectedState();
      }

      // The grid has a single option, read from the checked radio.
      updateOptions() {
        const checked = this.getCheckedInput();
        this.options = checked ? [checked.value] : [];
      }

      // Dropdown-only behaviour in VariantSelects; not needed for radios.
      updateDisabledOptions() {}

      // Only relevant for second and third options, which the grid never has.
      updateVariantStatuses() {}

      getCheckedInput() {
        return this.querySelector('.variant-shade-grid__input:checked');
      }

      syncSelectedState() {
        const checked = this.getCheckedInput();
        this.items.forEach((item) => {
          item.classList.toggle('selected', !!checked && item.contains(checked));
        });
        this.classList.remove('variant-shade-grid--error');

        if (this.selectedLabel) {
          this.selectedLabel.textContent = checked ? checked.value : this.selectedLabel.dataset.placeholder || '';
        }
      }

      onFilterClick(event) {
        event.preventDefault();
        this.setActiveGroup(event.currentTarget.dataset.group);
      }

      onFilterKeydown(event) {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        this.setActiveGroup(event.currentTarget.dataset.group);
      }

      // Scrolls the group tabs horizontally so the active one is visible,
      // without moving the page (same as the main PDP shade-selector).
      scrollActiveFilterIntoView() {
        const slider = this.querySelector('.filters');
        const active = this.querySelector('.filters li[aria-pressed="true"]');
        if (!slider || !active || slider.scrollWidth <= slider.clientWidth) return;

        const sliderRect = slider.getBoundingClientRect();
        const activeRect = active.getBoundingClientRect();
        if (activeRect.left >= sliderRect.left && activeRect.right <= sliderRect.right) return;

        slider.scrollLeft += activeRect.left - sliderRect.left;
      }

      setActiveGroup(group) {
        const showAll = !group || group === 'All';
        let matched = showAll;

        this.filterButtons.forEach((button) => {
          const isActive = button.dataset.group === group;
          if (isActive) matched = true;
          button.classList.toggle('button--secondary', !isActive);
          button.setAttribute('aria-pressed', String(isActive));
        });

        // Unknown group (e.g. a stale data attribute): fall back to All.
        if (!matched) {
          this.setActiveGroup('All');
          return;
        }

        this.items.forEach((item) => {
          item.hidden = !showAll && item.dataset.group !== group;
        });
      }

      onFormSubmit(event) {
        const form = event.target;
        if (!form || form.id !== `product-form-${this.dataset.section}`) return;
        if (this.getCheckedInput()) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        this.classList.add('variant-shade-grid--error');
        const productForm = form.closest('product-form');
        if (productForm && productForm.handleErrorMessage) {
          productForm.handleErrorMessage(this.dataset.errorMessage);
        }

        const firstVisible = this.items.find((item) => !item.hidden);
        const input = firstVisible && firstVisible.querySelector('.variant-shade-grid__input');
        if (input) input.focus({ preventScroll: true });
        this.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    customElements.define('variant-shade-grid', VariantShadeGrid);
  };

  if (customElements.get('variant-selects')) {
    defineVariantShadeGrid();
  } else {
    customElements.whenDefined('variant-selects').then(defineVariantShadeGrid);
  }
})();
